import React from 'react';
import PropTypes from 'prop-types';
import Truncate from 'react-truncate';
import moment from 'moment';
import numeral from 'numeral';
import noop from 'lodash/noop';
import isNull from 'lodash/isNull';
import isEmpty from 'lodash/isEmpty';
import findIndex from 'lodash/findIndex';
import isArray from 'lodash/isArray';

import { CLUSTER_AUTHOR_VALUES } from '../../constants';

import{
  formatTwitterUsername
} from '../../dataUtils';

import AddToGroupIcon from 'zignal-landscape/lib/Icon/AddToGroupIcon';
import GearsIcon from 'zignal-landscape/lib/Icon/GearsIcon';
import DiscoverIcon from 'zignal-landscape/lib/Icon/DiscoverIcon';
import ExternalLinkIcon from 'zignal-landscape/lib/Icon/ExternalLinkIcon';
import themeable from 'zignal-landscape/lib/themeable';
import Story from 'zignal-landscape/lib/Story';
import ToolTip from 'zignal-landscape/lib/ToolTip';
import MediaObject from 'zignal-landscape/lib/MediaObject';
import Link from 'zignal-landscape/lib/Link';
import TwitterIcon from 'zignal-landscape/lib/Icon/TwitterIcon';

const topOffset = 270;
const leftOffset = 180;

const _getStory = (stories, categoryId='', clusterBy) => {
  const [ clusterType, clusterLabel ] = categoryId.split('|');
  const type = clusterType || clusterBy;
  if (CLUSTER_AUTHOR_VALUES.includes(type)) {
    return stories[0] || {};
  }
  const index = findIndex(stories, (story) => {
    const labels = story[type] ? story[type] : [];
    const labelArr = isArray(labels) ? labels : [labels];
    // if categoryId, then find the story with the label (for category graph)
    // else we are hovering in the cluster graph and we want first story with data
    return categoryId
      ? labelArr.some((label) => label === clusterLabel.toString())
      : labelArr.length;
  });
  // ultimately default to first story if we can't find one for the data we are clustering by
  return stories[index >= 0 ? index : 0] || {};
};

const createQuotedTweet = (story, styles) => {
  const {
    originalQuotedText,
    originalQuotedUrl,
    originalAuthorName,
    retweetedAuthorUsername,
    originalCreatedAt
  } = story;

  if (!originalQuotedText) {
    /* v8 ignore next */
    return null;
  }

  return (
    <Story.EmbeddedStory onClick={/* v8 ignore next */ () =>  window.open(originalQuotedUrl, '_blank')}>
      <Story.Header>
        <MediaObject>
          <MediaObject.Content>
            <MediaObject.Header>
              {originalAuthorName}
            </MediaObject.Header>
            <MediaObject>
              <MediaObject.Icon>
                <TwitterIcon height="1em" width="1em" />
              </MediaObject.Icon>
              <MediaObject.Content>
                <MediaObject.Description>
                  <div className={styles.originalAuthorUsername}>
                    <Link
                      onClick={/* v8 ignore next */ (e)=> {
                        /* v8 ignore next */
                        e.stopPropagation();
                      }}
                      href={`https://twitter.com/${retweetedAuthorUsername}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className={styles.originalAuthorUsernameText}>{retweetedAuthorUsername}</span>
                    </Link>
                  </div>
                </MediaObject.Description>
              </MediaObject.Content>
            </MediaObject>
          </MediaObject.Content>
        </MediaObject>
      </Story.Header>
      <Story.Body>
        <Story.Text>
          <div className={styles.originalQuotedText}>
            <Truncate lines={2} ellipsis="...">{originalQuotedText}</Truncate>
          </div>
        </Story.Text>
      </Story.Body>
      <Story.Footer>
        <Story.Footer.Item>
          <div className={styles.originalCreatedAt}>
            <ToolTip tooltipText={moment(originalCreatedAt).format('DD MMMM, YYYY - hh:mm A')}>
              {moment(originalCreatedAt).fromNow()}
            </ToolTip>
          </div>
        </Story.Footer.Item>
      </Story.Footer>
    </Story.EmbeddedStory>
  );
};

const Tooltip = (props) => {
  const {
    styles = {},
    hoverMap = {},
    timezone,
    addToGroup = noop,
    searchDiscover,
    isUserDiscoverFeatureEnabled
  } = props;

  const { tooltipNode, categoryId, clusterBy } = hoverMap;
  if (!tooltipNode){
    return null;
  }
  const { position={}, data={} } = tooltipNode;
  const { top, left } = position;
  const { author={}, stories=[] } = data;
  const { username='', postCount=0, followerCount=0, automationScore } = author;
  const formattedUsername = formatTwitterUsername(username);
  const story = _getStory(stories, categoryId, clusterBy);
  const { createdAt, originalTitle, retweetedTitle, tweetId, body } = story;
  const title = originalTitle || retweetedTitle || body;
  const tweetMoment = moment(createdAt);
  let bodyContent = null;
  let isQuoted = false;

  // tooltip position
  const style = {
    top: top - topOffset,
    left: left - leftOffset
  };

  if(story.sourceSubtype === 'quote' && !isEmpty(story.originalQuotedText)){
    isQuoted = true;
    bodyContent = (<div className={styles.bodyQuotedContainer}>
      <div className={styles.quotedTitle}>
        <Truncate lines={2} ellipsis="...">{title}</Truncate>
      </div>
      <div className={styles.bodyQuoted}>
        {createQuotedTweet(story, styles)}
      </div>
    </div>);

    style.height=  '375px';
    style.top =  style.top - 120;
  }
  else{
    bodyContent = (<div className={styles.body}>
      <Truncate lines={4} ellipsis="...">{title}</Truncate>
    </div>);
  }

  if (timezone) {
    tweetMoment.tz(timezone);
  }
  const date = tweetMoment.format('MM/DD/YYYY');
  const time = tweetMoment.format('LT');
  const timezoneAbbr = tweetMoment.zoneAbbr();
  const postCountAbbr = postCount > 999 ? numeral(postCount).format('0.0a') : postCount;
  const followerCountAbbr = followerCount > 999 ? numeral(followerCount).format('0.0a') : followerCount;

  const linkToTwitter = () => {
    if (formattedUsername && tweetId){
      window.open(`https://twitter.com/${formattedUsername}/status/${tweetId}`);
    }
  };

  const onAddToGroupClick = (e) => {
    e && e.stopPropagation();
    // TODO change if Influence Intetelligence uses other sources
    addToGroup({
      type: 'author',
      targetName: 'twitter_author',
      targetLabel: 'Twitter Author',
      targetValue: formattedUsername
    });
  };

  return (
    <div style={style} className={styles.Tooltip}>
      <div className={isQuoted ? styles.arrowQuoted : styles.arrow}><div /></div>
      <div className={styles.header}>
        <div className={styles.authorContainer}>
          <span className={styles.username}>
            @{formattedUsername}
          </span>
          <div className={styles.authorMediaScoreContainer}>
            {isNull(automationScore)
              ? <p className={styles.authorMediaScoreUnknownContainer}>Unknown</p>
              : <div className={styles.authorMediaScoreValueContainer}>
                <p className={styles.authorMediaScoreValue}>{automationScore}</p>
                <GearsIcon className={styles.authorMediaScoreIcon} />
              </div>
            }
          </div>
        </div>
        <div className={styles.statsGroup}>
          <div className={styles.stats}><span className={styles.stat}>{postCountAbbr}</span><br />Posts</div>
          <div className={styles.stats}><span className={styles.stat}>{followerCountAbbr}</span><br />Followers</div>
          <div className={styles.stats}><span className={styles.stat}>{date}</span><br />{time} {timezoneAbbr}</div>
        </div>
      </div>
      {bodyContent}
      <div className={styles.footer}>
        <div className={styles.link} onClick={onAddToGroupClick} >
          <AddToGroupIcon /><span>Add to Group</span>
        </div>
        {isUserDiscoverFeatureEnabled &&
          <div className={styles.link} onClick={() => searchDiscover(formattedUsername)}>
            <DiscoverIcon /><span>Search in Discover</span>
          </div>
        }
        <div className={styles.link} onClick={linkToTwitter}>
          <ExternalLinkIcon /><span>View Original Story</span>
        </div>
      </div>
    </div>
  );
};

Tooltip.propTypes = {
  styles: PropTypes.object,
  hoverMap: PropTypes.object,
  timezone: PropTypes.string,
  addToGroup: PropTypes.func,
  searchDiscover: PropTypes.func,
  isUserDiscoverFeatureEnabled: PropTypes.bool
};

export default themeable(Tooltip);
